/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl';
import { useIntl } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { AppCard, type AppConnector } from './AppCard';
import { messages } from './messages';

const styles = cssMap({
	container: {
		width: '100%',
		color: token('color.text'),
	},
	headingWrapper: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		alignSelf: 'stretch',
	},
	heading: {
		margin: 0,
		maxWidth: '1200px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontFamily: "'Charlie Display', 'Charlie Text', sans-serif",
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: 1.5,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontWeight: 400,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '0.5px',
		color: token('color.text.subtle'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '2rem',
		width: '100%',
		'@media (max-width: 48rem)': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			fontSize: '32px',
		},
		'@media (max-width: 30rem)': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			fontSize: '24px',
		},
	},
	appsRowContainer: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-around',
		alignItems: 'center',
		alignSelf: 'stretch',
		gap: token('space.300'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingTop: '60px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBottom: '60px',
		width: '100%',
		'@media (max-width: 30rem)': {
			flexDirection: 'column',
			alignItems: 'flex-start',
			justifyContent: 'flex-start',
			gap: token('space.400'),
		},
	},
});

type RecommendedAppsProps = {
	notConnectedApps: AppConnector[];
};

export const RecommendedApps = ({ notConnectedApps }: RecommendedAppsProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={styles.container}>
			<Box xcss={styles.headingWrapper}>
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
				<p css={styles.heading}>
					<FormattedMessage {...messages.recommendedAppsHeading} />
				</p>
			</Box>
			<div css={styles.appsRowContainer}>
				{notConnectedApps
					.reverse()
					.slice(0, 2)
					.map((app) => (
						<AppCard key={app.displayName} {...app} usedBy={formatMessage(messages.usedByLabel)} />
					))}
			</div>
		</Box>
	);
};
